import { Button, CircularProgress } from '@mui/material';
import { useMemo, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import {
  ReportStatus,
  useUpdateReportStatusMutation,
  AllReportsListDocument,
  ReportDetailsQuery,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { REPORT_UPDATE_DIALOG_LABELS } from './labels';
import { Alert } from '@/components/shared/Alert';

type ReportDocument = ReportDetailsQuery['report'];

const getNextReportStatus = (currentStatus: ReportStatus) => {
  switch (currentStatus) {
    case ReportStatus.Draft:
      return ReportStatus.Published;
    case ReportStatus.Published:
      return ReportStatus.Closed;
    case ReportStatus.Closed:
      return ReportStatus.Draft;
    case ReportStatus.Deleted:
      return ReportStatus.Deleted;
    default:
      return ReportStatus.Draft;
  }
};

const showError = (message: string) => {
  return (
    <div>
      <Alert severity="error">{message}</Alert>
    </div>
  );
};

const showSuccess = (message: string) => {
  return <Alert severity="success">{message}</Alert>;
};

const showInfo = (message: string) => {
  return <Alert severity="info">{message}</Alert>;
};

const getUpdateButtonText = (status: ReportStatus) => {
  switch (status) {
    case ReportStatus.Draft:
      return 'Publish report';
    case ReportStatus.Published:
      return 'Close report';
    case ReportStatus.Closed:
      return '';
    default:
      return 'Publish report';
  }
};

export const ReportUpdator = () => {
  const setSnackbarMessage = useSetAtom(state.snackbarMessage);
  const [selectedReport, setSelectedReport] = useAtom(state.selectedReport);

  const [mutate, { loading, error }] = useUpdateReportStatusMutation({
    variables: {
      input: {
        reportId: selectedReport?.id ?? '',
        status: getNextReportStatus(selectedReport?.status || ReportStatus.Draft),
      },
    },
    refetchQueries: [AllReportsListDocument],
    onCompleted: (response) => {
      if (!response?.updateReportStatus?.id) {
        setSnackbarMessage({
          shouldShow: true,
          content: showError('Something went wrong'),
        });
        return;
      }
      const currentStatus = getNextReportStatus(selectedReport?.status || ReportStatus.Draft);
      setSnackbarMessage({
        shouldShow: true,
        content:
          currentStatus === ReportStatus.Closed
            ? showInfo('This report has been closed')
            : currentStatus === ReportStatus.Published
              ? showSuccess('Report has been published successfully')
              : showSuccess('Report updated successfully'),
      });
      setSelectedReport(
        (previous) =>
          ({
            ...previous,
            status: getNextReportStatus(selectedReport?.status || ReportStatus.Draft),
          }) as ReportDocument
      );
    },
    onError: (errorReponse) => {
      setSnackbarMessage({
        shouldShow: true,
        content: showError(errorReponse.message),
      });
    },
  });

  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const handleUpdateDialogClose = () => {
    setIsUpdateDialogOpen((previous) => !previous);
  };

  const confirmationDialogProps = useMemo(() => {
    const rest = REPORT_UPDATE_DIALOG_LABELS[selectedReport?.status || ReportStatus.Draft];
    return {
      buttonText: getUpdateButtonText(selectedReport?.status || ReportStatus.Draft),
      onConfirm: mutate,
      isOpen: isUpdateDialogOpen,
      handleClose: handleUpdateDialogClose,
      ...rest,
    };
  }, [selectedReport?.status, mutate, isUpdateDialogOpen]);

  const updateButtonText = useMemo(() => {
    return getUpdateButtonText(selectedReport?.status || ReportStatus.Draft);
  }, [selectedReport]);

  if (!selectedReport) return <></>;

  if (selectedReport.status === ReportStatus.Closed) return <></>;

  return (
    <>
      <Button
        variant={selectedReport?.status === ReportStatus.Published ? 'outlined' : 'contained'}
        size="small"
        onClick={handleUpdateDialogClose}
        disabled={loading}
      >
        {updateButtonText}
        {loading && !error && (
          <CircularProgress
            size={20}
            sx={{
              ml: 2,
              color: 'white',
            }}
          />
        )}
      </Button>
      <ConfirmationDialog {...confirmationDialogProps} />
    </>
  );
};
